import React, { useCallback } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MessageCircle } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { Button, LoadingSpinner, EmptyState, ErrorState } from '../common';
import { CommentItem } from './CommentItem';
import { CommentComposer } from './CommentComposer';
import { AdaptiveSheet, useSheetPresentationMode } from '../common/AdaptiveSheet';
import { useMaxSheetHeight } from '../../hooks/useMaxSheetHeight';
import { useCommentThread, type CommentWithPending } from '../../hooks/useCommentThread';

interface CommentsSheetProps {
  postId: string;
  postAuthorId: string;
  commentsCount: number;
  visible: boolean;
  onClose: () => void;
}

/**
 * Comment thread as a bottom sheet. Used below the desktop shell only — on
 * the shell the thread renders in the page (InlineComments), because a
 * centered dialog for a comment reads as an interruption.
 */
export function CommentsSheet({
  postId,
  postAuthorId: _postAuthorId,
  commentsCount,
  visible,
  onClose,
}: CommentsSheetProps) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const {
    comments,
    isLoading,
    isSending,
    loadError,
    commentText,
    setCommentText,
    editingCommentId,
    currentUserId,
    loadComments,
    loadMoreComments,
    handleSend,
    handleStartEdit,
    handleCancelEdit,
    handleDelete,
  } = useCommentThread(postId, visible);

  // Stable renderItem: the previous inline closure pair per row re-evaluated
  // every row's element tree on each keystroke in the composer.
  const renderComment = useCallback(
    ({ item }: { item: CommentWithPending }) => (
      <CommentItem
        comment={item}
        currentUserId={currentUserId}
        onEdit={handleStartEdit}
        onDelete={handleDelete}
      />
    ),
    [currentUserId, handleStartEdit, handleDelete]
  );

  return (
    <AdaptiveSheet
      isOpen={visible}
      variant="inline"
      snapPoints={['70%']}
      keyboardBehavior="fillParent"
      onRequestClose={onClose}
      onDismiss={onClose}
      dialogWidth={560}>
      {/* Wrap the sheet contents in an explicit flex column so the list
          consumes remaining space and the input pins to the bottom. Without
          this, on web the list overflowed past the sheet's visible area and
          the input rendered below the fold. Dialog mode needs a concrete
          height (a flex list inside a max-height card collapses on web). */}
      <CommentsBody>
        <View style={[styles.header, { borderBottomColor: colors.border }]}>
          <Text style={[styles.headerTitle, { color: colors.text }]}>
            Comments{commentsCount > 0 ? ` (${commentsCount})` : ''}
          </Text>
        </View>

        {isLoading ? (
          <View style={styles.listFill}>
            <LoadingSpinner message="Loading comments..." />
          </View>
        ) : loadError && comments.length === 0 ? (
          <View style={styles.listFill}>
            <ErrorState message={loadError} onRetry={loadComments} />
          </View>
        ) : (
          <AdaptiveSheet.FlatList
            data={comments}
            keyExtractor={(item) => item.id}
            renderItem={renderComment}
            style={styles.listFill}
            contentContainerStyle={[
              styles.listContent,
              comments.length === 0 && styles.emptyListContent,
            ]}
            onEndReached={loadMoreComments}
            onEndReachedThreshold={0.5}
            removeClippedSubviews
            initialNumToRender={15}
            maxToRenderPerBatch={10}
            windowSize={7}
            ListHeaderComponent={
              // Surface load-more failures without blowing away the existing
              // comments. Tapping retry re-runs the initial load.
              loadError && comments.length > 0 ? (
                <View style={[styles.loadMoreError, { backgroundColor: colors.dangerSoft }]}>
                  <Text style={[styles.loadMoreErrorText, { color: colors.danger }]}>
                    {loadError}
                  </Text>
                  <Button title="Retry" onPress={loadComments} variant="ghost" size="sm" />
                </View>
              ) : null
            }
            ListEmptyComponent={
              <EmptyState
                icon={MessageCircle}
                title="No comments yet"
                message="Be the first to comment."
              />
            }
          />
        )}

        <View
          style={[
            styles.inputContainer,
            {
              borderTopColor: colors.border,
              backgroundColor: colors.background,
              // Clear the iOS home-indicator area so the input + send button
              // aren't clipped by the bottom bezel. Keep a minimum of
              // SPACING.sm so Android / safe-area-less devices still have
              // breathing room.
              paddingBottom: Math.max(insets.bottom, SPACING.sm),
            },
          ]}>
          <CommentComposer
            value={commentText}
            onChangeText={setCommentText}
            onSend={handleSend}
            isSending={isSending}
            isEditing={Boolean(editingCommentId)}
            onCancelEdit={handleCancelEdit}
            inputComponent={AdaptiveSheet.TextInput}
          />
        </View>
      </CommentsBody>
    </AdaptiveSheet>
  );
}

/**
 * Presentation-aware content frame: flex column in sheet mode (the sheet
 * owns the height via its snap point), fixed height in dialog mode so the
 * internal list scrolls and the composer stays pinned.
 */
function CommentsBody({ children }: { children: React.ReactNode }) {
  const mode = useSheetPresentationMode();
  const insets = useSafeAreaInsets();
  const maxSheetHeight = useMaxSheetHeight();
  if (mode === 'dialog') {
    const height = Math.min(560, maxSheetHeight - insets.bottom);
    return <View style={[styles.sheetContent, { height }]}>{children}</View>;
  }
  return <View style={styles.sheetContent}>{children}</View>;
}

const styles = StyleSheet.create({
  sheetContent: {
    flex: 1,
  },
  listFill: {
    flex: 1,
  },
  header: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.smd,
    borderBottomWidth: 1,
  },
  headerTitle: {
    ...TYPOGRAPHY.h4,
    fontFamily: TYPOGRAPHY.label.fontFamily,
    textAlign: 'center',
  },
  listContent: {
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.md,
  },
  emptyListContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  inputContainer: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderTopWidth: 1,
  },
  loadMoreError: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    marginBottom: SPACING.sm,
    borderRadius: BORDER_RADIUS.md,
  },
  loadMoreErrorText: {
    flex: 1,
    ...TYPOGRAPHY.label,
  },
});
